/**
 * The product's data provider: the same store shape, filled from the real backend.
 *
 * The screens do not change. They read through the hooks in `provider.tsx`, and this fills those
 * hooks from `loadDatabase` — the read model in `from-api.ts` that maps the API's projections into
 * the shape the prototype's screens were written against. That is what makes the pixel acceptance
 * mean something: the components measured against the frozen reference are the components the
 * product renders, not a second implementation that happens to look like them.
 *
 * `update` here is an OPTIMISTIC LOCAL PATCH and nothing more. Nothing in this file writes to the
 * backend; writes go through `V07Commands` (`commands-api.ts`), which calls the authorized route
 * and then `refresh()`es this store from what the server actually holds. A screen that patched
 * locally and never refreshed would be showing a world that forgets — exactly what §11 forbids.
 */
import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { isApiError } from '../../api/errors';
import { listAssets, listFonts, readAssetContent } from '../../api/internal';
import { type RenderAsset, type RenderFont, registerRenderLibrary } from '../lib/render/library';
import { emptyDatabase } from './empty-db';
import { loadDatabase } from './from-api';
import { type DemoStore, V07StoreProvider } from './store';
import type { DemoDatabase } from './types';

/**
 * The empty world, before the first read answers.
 *
 * Every collection is empty and every settings field is blank or the value the SERVER is the
 * authority for once it answers. Nothing is imported from the demo seed: 450 KB of synthetic
 * credentials in the production bundle would be both a large download and a way for a fixture
 * record to appear, however briefly, in front of a real user.
 *
 * The four settings whose storage is still an open Product Owner decision (matrix H3, H4) keep the
 * conservative reading until there is somewhere to read them from: search engines excluded, an
 * unknown code a 404, a pending record's personal data hidden.
 */

/**
 * The render library, filled from the backend.
 *
 * The render plan resolves every layer against a content-addressed index and is fail-closed on the
 * digest: a reference whose sha256 no longer matches resolves to an error rather than to the stale
 * asset. Only the assets a drawing actually references are fetched — the bytes are the expensive
 * part, and an asset nothing draws does not need them.
 *
 * The bucket stays private throughout (PO 7.5 §2.4): the bytes come through the authenticated
 * `asset.read` route, the object URL never leaves the tab, and no bucket name, object path or
 * provider URL is ever exposed.
 */
async function registerLibraryFromApi(api: ApiClient, db: DemoDatabase): Promise<void> {
  const [assets, fonts] = await Promise.all([
    listAssets(api)
      .then((r) => r.assets)
      .catch(() => []),
    listFonts(api)
      .then((r) => r.fonts)
      .catch(() => []),
  ]);

  const wanted = new Set<string>();
  const collect = (layers: unknown): void => {
    for (const l of (layers ?? []) as { assetId?: string | null }[]) {
      if (l?.assetId) wanted.add(l.assetId);
    }
  };
  /*
   * The BACKGROUND is an asset too, and it was the one nothing fetched.
   *
   * It is not a layer — it lives at `layout.background.assetRef` — so a design whose artwork is a
   * background picture had its bytes left behind: the plan then failed «Görsel çözülemedi» and
   * every client-drawn surface of that template (the batch send preview, which draws rows no
   * record exists for yet) showed «Görsel doğrulanamadı» about a template that renders perfectly
   * on the server.
   */
  const collectBackground = (background: unknown): void => {
    const ref = (background as { assetRef?: { assetId?: string | null } } | null)?.assetRef;
    if (ref?.assetId) wanted.add(ref.assetId);
  };
  for (const t of db.templates) {
    collect(t.draft?.layers);
    collectBackground(t.draft?.background);
    for (const r of t.releases) {
      collect((r as { layers?: unknown }).layers);
      collectBackground((r as { background?: unknown }).background);
    }
  }

  const withBytes = await Promise.all(
    assets
      .filter((a) => wanted.has(a.id))
      .map(async (a) => {
        try {
          const { objectUrl } = await readAssetContent(api, a.id);
          return { asset: a, objectUrl };
        } catch {
          /* An asset the session may not read is simply not registered; the plan then fails
             closed on it and the screen says the layer could not be resolved. */
          return null;
        }
      }),
  );

  registerRenderLibrary({
    assets: withBytes
      .filter((x) => x !== null)
      .map(({ asset, objectUrl }) => ({
        id: asset.id,
        sha256: asset.sha256,
        mimeType: asset.mediaType,
        byteSize: asset.byteSize,
        width: asset.width,
        height: asset.height,
        aspectRatio: asset.height === 0 ? 1 : asset.width / asset.height,
        /* The ingestion pipeline records it; the list projection does not carry it, and the render
         path only asks whether to composite over a background. */
        hasAlpha: true,
        originalFileName: asset.originalFilename ?? asset.id,
        dataUrlForPrototype: objectUrl,
      })) satisfies RenderAsset[],
    fonts: fonts.map((f) => ({
      id: f.id,
      sha256: f.sha256,
      family: f.family,
      weight: f.weight,
      style: f.style,
    })) satisfies RenderFont[],
  });
}

export interface ApiDataState {
  /** True while a read is in flight — the first one and every refresh after a command. */
  loading: boolean;
  /**
   * True once the FIRST read has settled, and true forever after.
   *
   * The distinction matters: the screens must not mount over a world that has not arrived, but
   * they must not be torn down again every time a command refreshes it either. Unmounting on every
   * refresh destroyed the toast host along with the screen, so the message a button had just
   * raised — the whole point of the click — disappeared before anyone could read it.
   */
  ready: boolean;
  /** The backend's own message when the load failed. Never a stack trace. */
  error: string | null;
  reload: () => Promise<void>;
}

export function useApiStore(api: ApiClient): { store: DemoStore; state: ApiDataState } {
  const [db, setDb] = useState<DemoDatabase>(emptyDatabase);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* A load that answers after a newer one started must not overwrite it. */
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++generation.current;
    setLoading(true);
    try {
      const next = await loadDatabase(api, { settingsFallback: emptyDatabase().settings });
      if (generation.current !== mine) return;
      await registerLibraryFromApi(api, next);
      if (generation.current !== mine) return;
      setDb(next);
      setError(null);
    } catch (e) {
      if (generation.current !== mine) return;
      setError(isApiError(e) ? e.message : e instanceof Error ? e.message : 'Veriler okunamadı');
    } finally {
      if (generation.current === mine) {
        setLoading(false);
        setReady(true);
      }
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const update = useCallback((mutate: (draft: DemoDatabase) => void) => {
    setDb((current) => {
      const next = structuredClone(current);
      mutate(next);
      return next;
    });
  }, []);

  const store = useMemo<DemoStore>(
    () => ({
      db,
      update,
      /* The demo's «back to the seed» control is not a product control (§3). */
      reset: () => undefined,
    }),
    [db, update],
  );

  const state = useMemo<ApiDataState>(
    () => ({ loading, ready, error, reload }),
    [loading, ready, error, reload],
  );
  return { store, state };
}

export function ApiDataProvider({
  store,
  children,
}: {
  store: DemoStore;
  children: ReactNode;
}): ReactElement {
  return <V07StoreProvider store={store}>{children}</V07StoreProvider>;
}
